import styles from "./objects-table.module.css";
import { useNavigate } from "react-router-dom";
import { useContext, useMemo, useState, useCallback, useEffect } from "react";
import {
  Table,
  Flex,
  Button,
  Typography,
  Input,
  App,
  Pagination,
  Tooltip,
  Tag,
  Switch,
} from "antd";

import PencilAltIcon from "@/icons/PencilAltIcon";
import LockCloseIcon from "@/icons/LockClosedIcon";
import CheckIcon from "@/icons/CheckIcon";
import EditIcon from "@/icons/EditIcon";
import PlusIcon from "@/icons/PlusIcon";

import CustomLink from "@/components/ui/CustomLink";
import ContextMenu from "@/components/containers/ContextMenu";
import FieldCell from "@/components/tables/ObjectsTable/FieldCell";
import RelatedTasksTable from "@/components/tables/RelatedTasksTable";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import InfoCircleTooltip from "@/components/ui/InfoCircleTooltip";
import CustomSelect from "@/components/inputs/CustomSelect";
import ShowDeletedTasksSwitch from "@/components/inputs/ShowDeletedTasksSwitch";

import { UserContext } from "@/context/UserContext";
import { useColumnConfig } from "@/hooks/useColumnConfig";

import routes from "@/routes";

import { createObject, updateObject } from "@/api/objects";
import { formateISODateToMoment } from "@/utils";
import {
  formatDecimalValue,
  formatMoney,
  isIntegerInputInRange,
  isMoneyValueInRange,
  isNumberInputInRange,
  NUMBER_INPUT_RANGE_MESSAGE,
  sumDecimalValues,
  sumMoneyValues,
} from "@/utils/fieldValueConverter";

import {
  DEFINITIONS,
  NUMBER_FIELDS,
  UPLOAD_FIELDS,
} from "@/constants/definitions";

const NSPD_CONTEXT = {
  MANUAL_SYNC_FIELDS: "manual_sync_fields",
  MANUAL_OTHER_FIELDS: "manual_other_fields",
};

// Ключ для сохранения параметров колонок в локальном хранилище
const LOCAL_STORAGE_KEY = "objectTableColumnParameters";
const SUMMARY_ROW_UUID = "__summary-row__";

const hasRelatedTasksFlag = (record) => record?.has_related_tasks === true;
const isSummaryRow = (record) => record?.isSummaryRow === true;

// Метки для колонок, используемые в пользовательском интерфейсе
const COLUMN_LABELS = {
  name: "Название",
  unit: "Рабочая группа",
  author: "Автор",
  created_at: "Дата создания",
  updated_at: "Дата изменения",
};

const CREATE_MODE_INFO_TEXT =
  "Вы находитесь в режиме создания новой записи. Для перехода к следующей ячейке используйте Tab. Для перехода к предыдущей ячейке используйте Shift+Tab. После заполнения необходимых полей нажмите кнопку Создать.";

// Текст подсказки для режима редактирования
const EDIT_MODE_INFO_TEXT =
  "Для перехода к следующей ячейке используйте Tab. Для перехода к предыдущей ячейке используйте Shift+Tab. Для сохранения введенных данных используйте Enter. Для выхода из режима редактирования используйте Escape.";

const SUM_INFO_TEXT =
  "Сумма считается по отмеченным строкам текущей страницы. Если строки не отмечены, считается вся текущая страница.";

// Вычисляет порядок сортировки для заданного поля
const calcSortOrder = (sortedInfo, field) => {
  if (!sortedInfo) return null;
  const col = sortedInfo.find(({ columnKey }) => columnKey === field);
  return col ? col.order : null;
};

// Получает маршрут для просмотра объекта в зависимости от контекста
const getHref = (unit_uuid, isAdmins, definitionId, uuid) => {
  return unit_uuid
    ? routes.unitObjectDetail(unit_uuid, definitionId, uuid)
    : isAdmins
      ? routes.administration.objectDetail(definitionId, uuid)
      : routes.objectDetail(definitionId, uuid);
};

// Рендер иконки статуса доступа (заблокирован/редактируемый)
const renderReadOnlyIcon = (readOnly) =>
  readOnly ? (
    <LockCloseIcon className={styles.icon} />
  ) : (
    <CheckIcon className={styles.icon} />
  );

const hasExtendedResourceAccess = (field) => {
  const linkedDefinition =
    typeof field?.object === "object" && field.object !== null
      ? field.object
      : null;

  return Boolean(
    field?.definition?.use_permission_rules ||
    linkedDefinition?.use_permission_rules,
  );
};

const shouldResetLinkedFieldOnUnitChange = (field) =>
  field?.type === "object" && !hasExtendedResourceAccess(field);

const hasNonEditableClass = (className) =>
  typeof className === "string" &&
  className.split(" ").includes("non-editable-cell");

const isNavigationEditableColumn = (column, fieldsByName, isCreateMode) => {
  if (!column?.key || column.key === "actions" || column.key === "readOnly") {
    return false;
  }

  if (column.key === "author") return false;
  if (column.key === "unit") return isCreateMode;
  if (hasNonEditableClass(column.className)) return false;

  const field = fieldsByName[column.key];
  if (field) {
    return !["parent_polygon", "rpoint", "table"].includes(field.type);
  }

  return true;
};

// Утилита для установки фокуса на элемент в ячейке редактирования
const focusEditingCell = () => {
  const editingCellElement = document.querySelector(".editing-cell");

  if (editingCellElement) {
    let input = editingCellElement.querySelector(
      "input:not([type='file']), textarea, [contenteditable='true']",
    );

    if (!input) {
      input = editingCellElement.querySelector(
        "button[data-focus-edit-button='true']",
      );
    }

    if (input?.focus) {
      input.focus();
    }
  }
};

const formatSumValue = (value, fieldType) => {
  if (fieldType === "money") {
    return formatMoney(value);
  }

  return formatDecimalValue(value);
};

// Компонент таблицы объектов
const getUuidFromValue = (value) => {
  if (!value || typeof value !== "object") return value;

  return value.uuid ?? value.file_uuid ?? value.file?.uuid;
};

const normalizeReferenceValue = (value) => {
  if (!value || typeof value !== "object") return value;

  return getUuidFromValue(value) ?? value;
};

const ObjectsTable = ({
  title = "",
  objects,
  fields,
  pagination,
  loading,
  onChange,
  showUnit = false,
  showSelect = false,
  unit_uuid,
  definitionId,
  selectedRowKeys,
  onRowsSelect,
  getSelectCheckboxProps,
  sortedInfo,
  isAdmins = false,
  scroll = { x: "100%" },
  resize = false,
  color,
  units = [],
  onDeleteItem,
  onRefresh,
  canBeEdited = false,
  footerActions,
  isSortable = true,
  showEditActions = true,
  definitionType,
  showCreateButton = false,
  createDraftConfig,
  onLocalUpdate,
  isNspdEnabled = false,
  relatedTasksByObject = {},
  relatedTasksLoadingByObject = {},
  onLoadRelatedTasks,
  onSyncTableTask,
  syncingTaskUuids = [],
  showDeletedTasks = false,
  onShowDeletedTasksChange,
  showDeletedTasksPending = false,
}) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [isEdit, setIsEdit] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { rowUuid, columnKey }
  const [draftRow, setDraftRow] = useState(null);
  const [pendingCreate, setPendingCreate] = useState(false);
  const [showSum, setShowSum] = useState(false);
  const [localSelectedRowKeys, setLocalSelectedRowKeys] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const isCreateMode = !!draftRow;
  const createRowUuid = draftRow?.uuid;
  const isTableEditing = isEdit || isCreateMode;

  useEffect(() => {
    if (loading) {
      setExpandedRowKeys([]);
    }
  }, [loading]);

  // При переключении в режим создания, если мы были в режиме редактирования, то выключаем его, чтобы не было конфликтов
  useEffect(() => {
    if (isCreateMode && isEdit) {
      setIsEdit(false);
    }
  }, [isCreateMode, isEdit]);

  // При выходе из режима создания сбрасываем активную ячейку
  useEffect(() => {
    if (!isCreateMode) {
      setEditingCell(null);
    }
  }, [isCreateMode]);

  const handleDraftChange = useCallback((value) => {
    if (!value || typeof value !== "object") return;

    const nextValues = Object.entries(value).reduce((acc, [key, nextValue]) => {
      if (!key || typeof nextValue === "undefined") return acc;

      acc[key] = nextValue;
      return acc;
    }, {});

    if (!Object.keys(nextValues).length) return;

    setDraftRow((prev) => (prev ? { ...prev, ...nextValues } : prev));
  }, []);

  const handleDraftUnitChange = useCallback(
    (unitUuid) => {
      const nextUnit = units?.find((item) => item.uuid === unitUuid) || null;

      setDraftRow((prev) => {
        if (!prev) return prev;

        const nextDraft = {
          ...prev,
          unit_uuid: unitUuid || null,
          unit: nextUnit,
        };

        if (prev.unit_uuid === (unitUuid || null)) {
          return nextDraft;
        }

        fields.forEach((field) => {
          if (field?.field_name && shouldResetLinkedFieldOnUnitChange(field)) {
            nextDraft[field.field_name] = null;
          }
        });

        return nextDraft;
      });
    },
    [fields, units],
  );

  const handleCancelCreate = useCallback(() => {
    setDraftRow(null);
    setEditingCell(null);
    setPendingCreate(false);
  }, []);

  const createDraftRow = useCallback(
    (source = {}) => {
      const copiedUnitUuid = source.unit_uuid || source.unit?.uuid;
      const defaultUnitUuid = createDraftConfig?.objectUnitUuid || unit_uuid;
      const copiedUnit = units?.find((item) => item.uuid === copiedUnitUuid);
      const defaultUnit = !copiedUnitUuid
        ? units?.find((item) => item.uuid === defaultUnitUuid)
        : null;
      const selectedUnit = copiedUnit || defaultUnit || null;
      const relationFieldName = createDraftConfig?.relationFieldName;

      setDraftRow({
        ...source,
        uuid: `draft-${Date.now()}`,
        name: source.name ? `${source.name} - копия` : "",
        unit_uuid: selectedUnit?.uuid || null,
        unit: selectedUnit,
        author: null,
        created_at: null,
        updated_at: null,
        readOnly: false,
        definition: { uuid: createDraftConfig?.definitionUuid || definitionId },
        ...(relationFieldName
          ? { [relationFieldName]: createDraftConfig?.parentObjectUuid }
          : {}),
      });
    },
    [createDraftConfig, definitionId, unit_uuid, units],
  );

  const handleAddObject = useCallback(() => {
    if (!showCreateButton || isCreateMode || pendingCreate) return;

    createDraftRow({ name: "" });
  }, [showCreateButton, isCreateMode, pendingCreate, createDraftRow]);

  const getManualNspdContext = useCallback(
    (fieldName) => {
      const field = fields.find((item) => item.field_name === fieldName);

      if (!field?.nspd_key_uuid || !field?.nspd_key) {
        return NSPD_CONTEXT.MANUAL_OTHER_FIELDS;
      }

      return NSPD_CONTEXT.MANUAL_SYNC_FIELDS;
    },
    [fields],
  );

  const hasChangedNspdField = fields.some((field) => {
    if (!field?.field_name || !(field?.nspd_key_uuid && field?.nspd_key)) {
      return false;
    }

    return typeof draftRow?.[field.field_name] !== "undefined";
  });

  const nspdContext = hasChangedNspdField
    ? NSPD_CONTEXT.MANUAL_SYNC_FIELDS
    : NSPD_CONTEXT.MANUAL_OTHER_FIELDS;

  const handleCreateObject = useCallback(async () => {
    if (!draftRow || pendingCreate) return;

    const preparedName = draftRow?.name?.trim?.() || "";
    const relationFieldName = createDraftConfig?.relationFieldName;
    const relationValue = relationFieldName
      ? draftRow?.[relationFieldName]
      : null;
    const preparedUnitUuid = draftRow?.unit_uuid;
    const preparedDefinitionUuid =
      createDraftConfig?.definitionUuid || definitionId;

    if (!preparedName) {
      message.warning("Заполните название");
      return;
    }

    if (relationFieldName && !relationValue) {
      message.warning("Не заполнена связь с текущим объектом");
      return;
    }

    if (definitionType !== "repository" && !preparedUnitUuid) {
      message.warning("Не указана рабочая группа");
      return;
    }

    const invalidNumberField = fields.find((field) => {
      const value = draftRow?.[field.field_name];
      if (!NUMBER_FIELDS.includes(field.type)) return false;

      return field.type === "money"
        ? !isMoneyValueInRange(value)
        : field.type === "integer"
          ? !isIntegerInputInRange(value)
          : !isNumberInputInRange(value);
    });

    if (invalidNumberField) {
      message.warning(
        `${invalidNumberField.caption || invalidNumberField.name}: ${
          NUMBER_INPUT_RANGE_MESSAGE
        }`,
      );
      return;
    }

    const draftFieldValues = fields.reduce((acc, field) => {
      const fieldName = field?.field_name;
      if (!fieldName) return acc;

      let value = draftRow?.[fieldName];
      if (typeof value === "undefined") return acc;

      if (
        [...DEFINITIONS, ...UPLOAD_FIELDS].includes(field.type) &&
        value &&
        typeof value === "object"
      ) {
        value = getUuidFromValue(value) || null;
      }

      acc[fieldName] = value;
      return acc;
    }, {});

    setPendingCreate(true);
    try {
      await createObject({
        ...draftFieldValues,
        definition_uuid: preparedDefinitionUuid,
        name: preparedName,
        ...(relationFieldName ? { [relationFieldName]: relationValue } : {}),
        ...(preparedUnitUuid ? { unit_uuid: preparedUnitUuid } : {}),
        ...(isNspdEnabled ? { nspdContext } : {}),
      });

      setDraftRow(null);

      onRefresh?.();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setPendingCreate(false);
    }
  }, [
    draftRow,
    pendingCreate,
    createDraftConfig,
    definitionId,
    definitionType,
    message,
    fields,
    onRefresh,
    nspdContext,
    isNspdEnabled,
  ]);

  // Установка фокуса при смене активной ячейки
  useEffect(() => {
    if (isTableEditing && editingCell) {
      // Используем requestAnimationFrame чтобы гарантировать что элемент отрендерен
      requestAnimationFrame(() => {
        focusEditingCell();
      });
    }
  }, [editingCell, isTableEditing]);

  // Выход из режима редактирования таблицы по Esc,
  // если сейчас не активна ни одна ячейка
  useEffect(() => {
    if (!isTableEditing) return;

    const handleGlobalEscape = (event) => {
      if (event.key !== "Escape") return;

      setEditingCell(null);
      if (isCreateMode) {
        handleCancelCreate();
      } else {
        setIsEdit(false);
      }
    };

    window.addEventListener("keydown", handleGlobalEscape);
    return () => window.removeEventListener("keydown", handleGlobalEscape);
  }, [isTableEditing, isCreateMode, handleCancelCreate]);

  // Создает обработчики действий (копирование, просмотр, удаление) для элемента
  const createActionHandlers = useCallback(
    (item) => ({
      handleCopy: () => {
        createDraftRow(item.selfObject);
      },
      handleView: () => {
        navigate(getHref(item.unit_uuid, isAdmins, definitionId, item.uuid));
      },
      handleDelete: () => {
        onDeleteItem(item);
      },
    }),
    [navigate, isAdmins, definitionId, onDeleteItem, createDraftRow],
  );

  const tableObjects = useMemo(() => {
    if (!draftRow) return objects;
    return [draftRow, ...objects];
  }, [draftRow, objects]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 960);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Формирует базовые колонки таблицы с фиксированными и динамическими полями
  const baseColumns = useMemo(
    () => [
      {
        title: <PencilAltIcon className={styles.pencilIcon} />,
        dataIndex: "readOnly",
        key: "readOnly",
        width: 50,
        fixed: !isMobile ? "left" : false,
        align: "center",
        className: "non-editable-cell",
        ellipsis: true,
        render: (_, record) =>
          isSummaryRow(record) ? null : renderReadOnlyIcon(record?.readOnly),
      },
      {
        title: "Название",
        dataIndex: "name",
        key: "name",
        width: 200,
        fixed: !isMobile ? "left" : false,
        ellipsis: true,
        sortOrder: calcSortOrder(sortedInfo, "name"),
        sorter: isSortable,
        onCell: (record) => ({
          className:
            editingCell?.rowUuid === record.uuid &&
            editingCell?.columnKey === "name"
              ? "editing-cell"
              : "",
        }),
        render: (_, record) => {
          if (isSummaryRow(record)) {
            return record.name;
          }

          const { uuid, name, readOnly } = record;
          const isCreateRow = isCreateMode && uuid === createRowUuid;
          const isEditing =
            isTableEditing &&
            editingCell?.rowUuid === uuid &&
            editingCell?.columnKey === "name" &&
            !readOnly;
          return isEditing ? (
            <Input
              value={isCreateRow ? name || "" : undefined}
              defaultValue={isCreateRow ? undefined : name}
              className="w-100"
              onChange={(e) => {
                if (isCreateRow) {
                  handleDraftChange({ name: e.target.value });
                }
              }}
              onKeyDown={(e) => {
                // не даем сохранить пустое название
                if (e.key === "Enter" && !e.target.value) return;

                handleKeyPress(e, uuid, { name: e.target.value });
              }}
            />
          ) : isTableEditing ? (
            name
          ) : (
            <CustomLink to={getHref(unit_uuid, isAdmins, definitionId, uuid)}>
              {name}
            </CustomLink>
          );
        },
      },
      ...(showUnit
        ? [
            {
              title: "Рабочая группа",
              dataIndex: "unit",
              key: "unit",
              width: 200,
              ellipsis: {
                showTitle: false,
              },
              sortOrder: calcSortOrder(sortedInfo, "unit"),
              sorter: isSortable,
              onCell: (record) => ({
                className:
                  isCreateMode && record.uuid === createRowUuid
                    ? editingCell?.rowUuid === record.uuid &&
                      editingCell?.columnKey === "unit"
                      ? "editing-cell"
                      : ""
                    : "non-editable-cell",
              }),
              render: (unit, record) => {
                if (isSummaryRow(record)) {
                  return null;
                }

                const { uuid } = record;
                const isCreateRow = isCreateMode && uuid === createRowUuid;

                if (!isCreateRow) {
                  return unit?.name ? <div>{unit?.name}</div> : "-";
                }

                return (
                  <CustomSelect
                    value={draftRow?.unit_uuid}
                    options={units}
                    fieldNames={{ value: "uuid", label: "name" }}
                    placeholder="Выберите рабочую группу"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.name || "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    onChange={handleDraftUnitChange}
                    disabled={pendingCreate}
                    style={{ width: "100%" }}
                  />
                );
              },
            },
          ]
        : []),
      {
        title: "Автор",
        dataIndex: "author",
        key: "author",
        width: 180,
        ellipsis: { showTitle: false },
        render: (_, record) => {
          if (isSummaryRow(record)) {
            return null;
          }

          const { author } = record;
          if (!author) return;

          return (
            <EmployeeProfile
              {...author}
              fullName={false}
              avatarSize={24}
              tooltip
            />
          );
        },
        className: "non-editable-cell",
      },
      {
        title: "Дата создания",
        dataIndex: "created_at",
        key: "created_at",
        width: 180,
        ellipsis: true,
        sortOrder: calcSortOrder(sortedInfo, "created_at"),
        sorter: isSortable,
        render: (created_at, record) => {
          if (isSummaryRow(record)) {
            return null;
          }

          if (isCreateMode && record.uuid === createRowUuid) {
            return null;
          }

          return created_at ? formateISODateToMoment(created_at) : "-";
        },
        className: "non-editable-cell",
      },
      {
        title: "Дата изменения",
        dataIndex: "updated_at",
        key: "updated_at",
        width: 180,
        ellipsis: true,
        sortOrder: calcSortOrder(sortedInfo, "updated_at"),
        sorter: isSortable,
        render: (updated_at, record) => {
          if (isSummaryRow(record)) {
            return null;
          }

          if (isCreateMode && record.uuid === createRowUuid) {
            return null;
          }

          return updated_at ? formateISODateToMoment(updated_at) : "-";
        },
        className: "non-editable-cell",
      },

      ...fields
        .filter(({ is_visible, type }) => is_visible && type !== "table")
        .sort((a, b) => a.idx - b.idx)
        .map((field) => ({
          title: (
            <Flex align="center" gap={6} style={{ minWidth: 0 }}>
              <span
                style={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={field.caption || field.name}
              >
                {field.caption || field.name}
              </span>

              {field.nspd_key_uuid && field.nspd_key && (
                <Tooltip title="Поле участвует в синхронизации данных из НСПД">
                  <Tag
                    color="blue"
                    style={{
                      marginInlineEnd: 0,
                      flexShrink: 0,
                    }}
                  >
                    НСПД
                  </Tag>
                </Tooltip>
              )}
            </Flex>
          ),
          field: field.field,
          dataIndex: field.uuid,
          key: field.field_name,
          width: 250,
          ellipsis: true,
          sortOrder: calcSortOrder(sortedInfo, field.field_name),
          sorter: isSortable,
          className: field.type === "parent_polygon" ? "non-editable-cell" : "",
          onCell: (record) => ({
            className:
              editingCell?.rowUuid === record.uuid &&
              editingCell?.columnKey === field.field_name
                ? "editing-cell"
                : "",
          }),
          render: (val, record) => {
            if (isSummaryRow(record)) {
              return val ?? null;
            }

            const { uuid, unit, definition, readOnly } = record;
            return (
              <FieldCell
                val={val}
                field={field}
                isEditMode={isTableEditing}
                isCurrentEdit={
                  editingCell?.rowUuid === uuid &&
                  editingCell?.columnKey === field.field_name &&
                  !readOnly
                }
                unit_uuid={unit_uuid}
                units={units}
                object_uuid={uuid}
                object_unit_uuid={unit?.uuid}
                definition_uuid={definition?.uuid}
                isAdmins={isAdmins}
                color={color}
                onChange={(value) => {
                  if (isCreateMode && uuid === createRowUuid) {
                    handleDraftChange({ [field.field_name]: value });
                  }
                }}
                onKeyDown={(e, value) => {
                  handleKeyPress(e, uuid, { [field.field_name]: value });
                }}
                onComplete={(value) => {
                  saveAndMoveNext(uuid, { [field.field_name]: value });
                }}
                canEditObject={!readOnly && field.type !== "parent_polygon"}
                valueAsObject={true}
              />
            );
          },
        })),
      ...(isTableEditing
        ? []
        : [
            {
              title: "",
              width: 48,
              align: "center",
              fixed: "right",
              key: "actions",
              render: (_, item) => {
                if (isSummaryRow(item)) {
                  return null;
                }

                const { handleCopy, handleView, handleDelete } =
                  createActionHandlers(item);

                return (
                  <ContextMenu
                    vertical
                    onCopy={
                      definitionType !== "repository" ? handleCopy : undefined
                    }
                    onView={handleView}
                    onDelete={
                      showEditActions && !item.readOnly
                        ? handleDelete
                        : undefined
                    }
                  />
                );
              },
            },
          ]),
    ],
    // eslint-disable-next-line
    [
      fields,
      showUnit,
      unit_uuid,
      definitionId,
      sortedInfo,
      isAdmins,
      color,
      isTableEditing,
      editingCell,
      createActionHandlers,
      isCreateMode,
      createRowUuid,
      handleDraftChange,
      handleDraftUnitChange,
      draftRow,
      pendingCreate,
      units,
    ],
  );

  // Метаданные для динамических колонок (пользовательские поля)
  const dynamicColumnsMeta = useMemo(
    () =>
      fields.map((f) => ({
        key: f.field_name,
        label: f.caption || f.name,
        defaultVisible: f.is_visible,
      })),
    [fields],
  );

  // Получает итоговые колонки с применением пользовательских настроек видимости
  const { columns, components } = useColumnConfig(
    baseColumns,
    user.user_uuid,
    `${LOCAL_STORAGE_KEY}_${definitionId}`,
    {
      resize,
      dynamicColumns: dynamicColumnsMeta,
      columnLabels: COLUMN_LABELS,
    },
  );

  // Преобразует исходные данные в формат для таблицы, извлекая нужные поля
  const dataSource = useMemo(() => {
    return tableObjects.map((object) =>
      fields.reduce(
        (acc, field) => {
          acc[field.uuid] = object[field.field_name];
          return acc;
        },
        {
          uuid: object.uuid,
          name: object.name,
          unit: object.unit,
          author: object.author,
          created_at: object.created_at,
          updated_at: object.updated_at,
          readOnly: object.readOnly, // Флаг, указывающий, является ли объект только для чтения
          has_related_tasks: object.has_related_tasks,
          selfObject: object, // Сохраняет объект для дальнейших операций (копирование и т.д.)
        },
      ),
    );
  }, [tableObjects, fields]);

  const visibleSummableFields = useMemo(() => {
    const visibleColumnKeys = new Set(columns.map((column) => column.key));

    return fields.filter(
      ({ field_name, type }) =>
        NUMBER_FIELDS.includes(type) && visibleColumnKeys.has(field_name),
    );
  }, [columns, fields]);

  const currentPageRowKeys = useMemo(
    () => dataSource.map(({ uuid }) => uuid),
    [dataSource],
  );

  const effectiveSelectedRowKeys = useMemo(
    () => (showSelect ? selectedRowKeys || [] : localSelectedRowKeys),
    [showSelect, selectedRowKeys, localSelectedRowKeys],
  );

  const selectedCurrentPageRowKeys = useMemo(() => {
    const currentPageRowKeysSet = new Set(currentPageRowKeys);
    return effectiveSelectedRowKeys.filter((key) =>
      currentPageRowKeysSet.has(key),
    );
  }, [currentPageRowKeys, effectiveSelectedRowKeys]);

  const sumRows = useMemo(() => {
    if (!selectedCurrentPageRowKeys.length) return dataSource;

    const selectedCurrentPageRowKeysSet = new Set(selectedCurrentPageRowKeys);
    return dataSource.filter(({ uuid }) =>
      selectedCurrentPageRowKeysSet.has(uuid),
    );
  }, [dataSource, selectedCurrentPageRowKeys]);

  const sumsByFieldName = useMemo(() => {
    return visibleSummableFields.reduce((acc, field) => {
      acc[field.field_name] =
        field.type === "money"
          ? sumMoneyValues(sumRows.map((row) => row[field.uuid]))
          : sumDecimalValues(sumRows.map((row) => row[field.uuid]));
      return acc;
    }, {});
  }, [sumRows, visibleSummableFields]);

  const displayDataSource = useMemo(() => {
    if (isTableEditing || !showSum || !visibleSummableFields.length) {
      return dataSource;
    }

    const summaryRow = visibleSummableFields.reduce(
      (acc, field) => {
        acc[field.uuid] = formatSumValue(
          sumsByFieldName[field.field_name],
          field.type,
        );
        return acc;
      },
      {
        uuid: SUMMARY_ROW_UUID,
        name: "Сумма",
        readOnly: true,
        isSummaryRow: true,
      },
    );

    return [summaryRow, ...dataSource];
  }, [
    dataSource,
    isTableEditing,
    showSum,
    sumsByFieldName,
    visibleSummableFields,
  ]);

  useEffect(() => {
    if (!showSum) {
      setLocalSelectedRowKeys([]);
    }
  }, [showSum]);

  // Определяет класс для строк (выделяет заблокированные строки в режиме редактирования)
  const rowClassName = useCallback(
    (record) => {
      if (isSummaryRow(record)) {
        return `${styles.summaryRow} top-align`;
      }

      const isNewRow = isCreateMode && record.uuid === createRowUuid;

      const classes = ["top-align"];
      if (isEdit || isNewRow) {
        classes.push("editable-row");
        if (record.readOnly) {
          classes.push("disabled-row");
        }
      }

      if (isNewRow) {
        classes.push(styles.draftRow);
      }

      return classes.join(" ");
    },
    [isEdit, isCreateMode, createRowUuid],
  );

  // Конфигурация выделения строк (чекбоксы) при выключенном режиме редактирования
  const getRowSelectionCheckboxProps = useCallback(
    (record) => {
      if (isSummaryRow(record)) {
        return {
          disabled: true,
        };
      }

      return getSelectCheckboxProps?.(record) || {};
    },
    [getSelectCheckboxProps],
  );

  const rowSelection = useMemo(
    () =>
      (showSelect || showSum) && !isTableEditing
        ? {
            type: "checkbox",
            selectedRowKeys: effectiveSelectedRowKeys,
            preserveSelectedRowKeys: showSelect,
            getCheckboxProps: getRowSelectionCheckboxProps,
            onChange: (nextSelectedRowKeys, nextSelectedRows) => {
              if (showSelect) {
                onRowsSelect?.(nextSelectedRowKeys, nextSelectedRows);
              } else {
                setLocalSelectedRowKeys(nextSelectedRowKeys);
              }
            },
          }
        : undefined,
    [
      showSelect,
      showSum,
      isTableEditing,
      effectiveSelectedRowKeys,
      onRowsSelect,
      getRowSelectionCheckboxProps,
    ],
  );

  const expandableConfig = useMemo(() => {
    if (isTableEditing || !onLoadRelatedTasks) {
      return undefined;
    }

    return {
      expandedRowKeys,
      onExpandedRowsChange: setExpandedRowKeys,
      rowExpandable: hasRelatedTasksFlag,
      onExpand: (expanded, record) => {
        if (
          !expanded ||
          relatedTasksByObject[record.uuid] ||
          relatedTasksLoadingByObject[record.uuid]
        ) {
          return;
        }
        onLoadRelatedTasks(record);
      },
      expandedRowRender: (record) => (
        <div className={styles.relatedTasks}>
          {/* <Typography.Text strong>Связанные задачи</Typography.Text> */}
          <RelatedTasksTable
            className={styles.relatedTasksTable}
            data={relatedTasksByObject[record.uuid] || []}
            loading={!!relatedTasksLoadingByObject[record.uuid]}
            unit_uuid={unit_uuid}
            isAdmins={isAdmins}
            onSyncTableTask={(task) => onSyncTableTask?.(record, task)}
            syncingTaskUuids={syncingTaskUuids}
          />
        </div>
      ),
    };
  }, [
    isAdmins,
    expandedRowKeys,
    isTableEditing,
    onLoadRelatedTasks,
    relatedTasksByObject,
    relatedTasksLoadingByObject,
    onSyncTableTask,
    syncingTaskUuids,
    unit_uuid,
  ]);

  const fieldsByName = useMemo(
    () =>
      fields.reduce((acc, field) => {
        if (field?.field_name) {
          acc[field.field_name] = field;
        }

        return acc;
      }, {}),
    [fields],
  );

  const editableColumns = useMemo(
    () =>
      columns.filter((col) =>
        isNavigationEditableColumn(col, fieldsByName, isCreateMode),
      ),
    [columns, fieldsByName, isCreateMode],
  );

  // При появлении черновой строки ставим фокус в первую доступную редактируемую ячейку
  useEffect(() => {
    if (
      !createRowUuid ||
      !tableObjects.some((item) => item.uuid === createRowUuid)
    ) {
      return;
    }

    // Не перезаписываем текущую колонку в create-mode при каждом обновлении данных строки.
    if (editingCell?.rowUuid === createRowUuid) {
      return;
    }

    const firstEditableColumnKey = editableColumns[0]?.key;
    if (!firstEditableColumnKey) {
      setEditingCell(null);
      return;
    }

    setEditingCell({
      rowUuid: createRowUuid,
      columnKey: firstEditableColumnKey,
    });
  }, [createRowUuid, tableObjects, editingCell, editableColumns]);

  // Переключает режим редактирования таблицы
  const toggleEditMode = useCallback(() => {
    if (isCreateMode) {
      handleCancelCreate();
      return;
    }

    setIsEdit((prev) => {
      if (!prev) {
        // При включении режима редактирования, ищем первую не-readOnly строку
        const firstEditableRecord = dataSource.find((item) => !item.readOnly);
        const firstEditableColumnKey = editableColumns[0]?.key;

        setEditingCell(
          firstEditableRecord && firstEditableColumnKey
            ? {
                rowUuid: firstEditableRecord.uuid,
                columnKey: firstEditableColumnKey,
              }
            : null,
        );
        // Фокус установится через useEffect при изменении editingCell
      } else {
        // При отключении режима, закрываем редактирование
        setEditingCell(null);
      }
      return !prev;
    });
  }, [dataSource, editableColumns, isCreateMode, handleCancelCreate]);

  // Обработчик клика по ячейке для активации редактирования
  const handleCellClick = useCallback(
    (record, event) => {
      if (
        !isTableEditing ||
        isSummaryRow(record) ||
        record.readOnly ||
        (isCreateMode && record.uuid !== createRowUuid)
      )
        return;

      // Получаем элемент ячейки из клика
      const cell = event.target.closest(".ant-table-cell");
      if (!cell) return;

      // Проверяем, что это не non-editable ячейка
      if (cell.classList.contains("non-editable-cell")) return;

      // Получаем ключ колонки из data-атрибута или класса
      const columnIndex = Array.from(cell.parentElement.children).indexOf(cell);
      const column = columns[columnIndex];

      if (column?.key && column.key !== "actions") {
        setEditingCell({ rowUuid: record.uuid, columnKey: column.key });
        // Фокус установится через useEffect при изменении editingCell
      }
    },
    [isTableEditing, columns, isCreateMode, createRowUuid],
  );

  // Переход к следующей ячейке (для навигации по Tab)
  const moveToNextCell = useCallback(() => {
    if (!editingCell) return;

    const { rowUuid, columnKey } = editingCell;

    // Находим текущий индекс колонки
    const currentColIndex = editableColumns.findIndex(
      (col) => col.key === columnKey,
    );

    // Если есть следующая колонка в текущей строке
    if (currentColIndex < editableColumns.length - 1) {
      setEditingCell({
        rowUuid,
        columnKey: editableColumns[currentColIndex + 1].key,
      });
      return;
    }

    if (isCreateMode) {
      // В режиме создания остаемся в пределах одной строки
      return;
    }

    // Иначе ищем следующую редактируемую строку
    const rowIndex = dataSource.findIndex((item) => item.uuid === rowUuid);
    let nextRowIndex = rowIndex + 1;
    while (nextRowIndex < dataSource.length) {
      if (!dataSource[nextRowIndex].readOnly) {
        // Переходим на первую редактируемую колонку следующей строки
        setEditingCell({
          rowUuid: dataSource[nextRowIndex].uuid,
          columnKey: editableColumns[0].key,
        });
        return;
      }
      nextRowIndex++;
    }

    // Если это была последняя строка - завершаем редактирование
    setIsEdit(false);
    setEditingCell(null);
  }, [editingCell, editableColumns, dataSource, isCreateMode]);

  // Переход к предыдущей ячейке (для навигации по Shift+Tab)
  const moveToPreviousCell = useCallback(() => {
    if (!editingCell) return;

    const { rowUuid, columnKey } = editingCell;

    // Находим текущий индекс колонки
    const currentColIndex = editableColumns.findIndex(
      (col) => col.key === columnKey,
    );

    // Если есть предыдущая колонка в текущей строке
    if (currentColIndex > 0) {
      setEditingCell({
        rowUuid,
        columnKey: editableColumns[currentColIndex - 1].key,
      });
      return;
    }

    if (isCreateMode) {
      // В режиме создания остаемся в пределах одной строки
      return;
    }

    // Иначе ищем предыдущую редактируемую строку
    const rowIndex = dataSource.findIndex((item) => item.uuid === rowUuid);
    let prevRowIndex = rowIndex - 1;
    while (prevRowIndex >= 0) {
      if (!dataSource[prevRowIndex].readOnly) {
        // Переходим на последнюю редактируемую колонку предыдущей строки
        setEditingCell({
          rowUuid: dataSource[prevRowIndex].uuid,
          columnKey: editableColumns[editableColumns.length - 1].key,
        });
        return;
      }
      prevRowIndex--;
    }

    // Если это была первая строка - ничего не делаем
  }, [editingCell, editableColumns, dataSource, isCreateMode]);

  // Функция для сохранения изменений и перехода к следующей ячейке
  const saveAndMoveNext = useCallback(
    async (uuid, value) => {
      if (isCreateMode && uuid === createRowUuid) {
        handleDraftChange(value);
        moveToNextCell();
        return;
      }

      const record = objects.find((obj) => obj.uuid === uuid);
      const [key, newValue] = Object.entries(value)[0] || [];
      const oldValue = record?.[key];

      const isEqual =
        normalizeReferenceValue(newValue) === normalizeReferenceValue(oldValue);

      if (isEqual) {
        moveToNextCell();
        return;
      }
      onLocalUpdate?.(uuid, value);
      try {
        await updateObject({
          uuid: record.uuid,
          definition_uuid: definitionId,
          [key]: normalizeReferenceValue(newValue),
          ...(isNspdEnabled ? { nspdContext: getManualNspdContext(key) } : {}),
        });

        message.success("Изменения сохранены");

        // Обновляем данные таблицы после успешного сохранения
        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        onLocalUpdate?.(uuid, { [key]: record[key] });
        message.open({
          type: "error",
          content: error.message,
          duration: 10,
        });
      }

      moveToNextCell();
    },
    [
      moveToNextCell,
      objects,
      definitionId,
      onRefresh,
      message,
      isCreateMode,
      createRowUuid,
      handleDraftChange,
      getManualNspdContext,
      isNspdEnabled,
      onLocalUpdate,
    ],
  );

  // Обработчик нажатий для навигации между ячейками
  const handleKeyPress = useCallback(
    async (e, uuid, value) => {
      if (e.key === "Tab") {
        e.preventDefault();

        if (isCreateMode && uuid === createRowUuid) {
          handleDraftChange(value);
        }

        // если Shift + Tab - переходим к предыдущей ячейке
        if (e.shiftKey) {
          moveToPreviousCell();
        } else {
          moveToNextCell();
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        await saveAndMoveNext(uuid, value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setEditingCell(null);
      }
      // Для стрелок и других клавиш - пропускаем их дальше
    },
    [
      moveToNextCell,
      moveToPreviousCell,
      saveAndMoveNext,
      isCreateMode,
      createRowUuid,
      handleDraftChange,
    ],
  );

  const handleTableKeyDownCapture = useCallback(
    (event) => {
      if (
        event.key !== "Tab" ||
        !isTableEditing ||
        !event.target.closest?.(".editing-cell")
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.shiftKey) {
        moveToPreviousCell();
      } else {
        moveToNextCell();
      }
    },
    [isTableEditing, moveToNextCell, moveToPreviousCell],
  );

  const handlePaginationChange = (current, pageSize) => {
    window.__MAP_SCREENSHOT_RENDERER__?.reset();
    setExpandedRowKeys([]);

    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== pagination.pageSize;

    const newPagination = {
      ...pagination,
      current: shouldResetPage ? 1 : current,
      pageSize,
    };

    onChange(newPagination, null, sortedInfo);
  };

  const handleTableChange = (...args) => {
    setExpandedRowKeys([]);
    onChange?.(...args);
  };

  const shouldShowSumToggle =
    !isTableEditing && !!dataSource?.length && !!visibleSummableFields.length;

  return (
    <Flex vertical gap={10}>
      <Flex
        gap={10}
        align="start"
        justify="space-between"
        className={`table-actions ${styles.actions}`}
      >
        <Flex vertical gap={5}>
          {title && (
            <Typography.Text
              style={{
                paddingBlock: 5,
              }}
            >
              {title}
            </Typography.Text>
          )}

          {isTableEditing && (
            <Typography.Text type="secondary" className={styles.infoText}>
              {isCreateMode ? CREATE_MODE_INFO_TEXT : EDIT_MODE_INFO_TEXT}
            </Typography.Text>
          )}
        </Flex>

        <Flex
          gap={12}
          className={[
            styles.actionsRight,
            isCreateMode ? styles.actionsRightCreating : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Flex gap={12} className={styles.createActions}>
            {isCreateMode ? (
              <>
                <Button
                  type="primary"
                  loading={pendingCreate}
                  onClick={handleCreateObject}
                >
                  Создать
                </Button>
                <Button
                  color="primary"
                  variant="link"
                  disabled={pendingCreate}
                  onClick={handleCancelCreate}
                >
                  Отменить создание
                </Button>
              </>
            ) : (
              showCreateButton && (
                <Button
                  color="primary"
                  variant="link"
                  icon={<PlusIcon />}
                  className={`no-padding ${styles.button}`}
                  disabled={isCreateMode || pendingCreate}
                  onClick={handleAddObject}
                >
                  Создать
                </Button>
              )
            )}
          </Flex>

          {canBeEdited && !!dataSource?.length && !isCreateMode && (
            <Button
              color="primary"
              variant="link"
              className={`no-padding ${styles.button} ${styles.editAction}`}
              onClick={toggleEditMode}
            >
              {isEdit ? (
                "Завершить редактирование"
              ) : (
                <>
                  <EditIcon /> Редактировать данные
                </>
              )}
            </Button>
          )}

          {(shouldShowSumToggle || onShowDeletedTasksChange) && (
            <Flex vertical gap={6} className={styles.toggleGroup}>
              {shouldShowSumToggle && (
                <Flex align="center" gap={6} className={styles.sumToggle}>
                  <Switch
                    size="small"
                    checked={showSum}
                    disabled={isTableEditing}
                    onChange={setShowSum}
                  />
                  <Typography.Text>Показать сумму</Typography.Text>
                  <InfoCircleTooltip
                    placement={isMobile ? "top" : "topRight"}
                    size={16}
                  >
                    {SUM_INFO_TEXT}
                  </InfoCircleTooltip>
                </Flex>
              )}

              {onShowDeletedTasksChange && (
                <ShowDeletedTasksSwitch
                  checked={showDeletedTasks}
                  onChange={onShowDeletedTasksChange}
                  disabled={showDeletedTasksPending}
                  size="small"
                />
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      <div onKeyDownCapture={handleTableKeyDownCapture}>
        <Table
          dataSource={displayDataSource}
          columns={columns}
          rowKey="uuid"
          loading={loading}
          pagination={false}
          onChange={handleTableChange}
          showSorterTooltip={{
            target: "sorter-icon",
            title: "Нажмите для сортировки",
          }}
          scroll={scroll}
          rowClassName={rowClassName}
          rowSelection={rowSelection}
          expandable={expandableConfig}
          components={resize ? components : undefined}
          tableLayout="fixed"
          bordered={resize}
          onRow={(record) => ({
            onClick: (event) => handleCellClick(record, event),
          })}
        />
      </div>

      <Flex justify="space-between" align="center" className={styles.footer}>
        {pagination && (
          <Pagination {...pagination} onChange={handlePaginationChange} />
        )}
        <div className={styles.footerRight}>
          {typeof footerActions === "function"
            ? footerActions({ isCreateMode, pendingCreate })
            : footerActions}
        </div>
      </Flex>
    </Flex>
  );
};

export default ObjectsTable;
